// src/services/SanitizationService.js - NEW FILE
// Comprehensive input sanitization service that enhances existing ValidationService

import { ErrorTrackingService } from './ErrorTrackingService';

export class SanitizationService {
  
  // XSS Protection Patterns
  static XSS_PATTERNS = [
    // Script tags and javascript: protocol
    /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi,
    /javascript:/gi,
    /vbscript:/gi,
    /onload\s*=/gi,
    /onerror\s*=/gi,
    /onclick\s*=/gi,
    /onmouseover\s*=/gi,
    
    // Common XSS vectors
    /<iframe\b[^>]*>/gi,
    /<object\b[^>]*>/gi,
    /<embed\b[^>]*>/gi,
    /<form\b[^>]*>/gi,
    /<input\b[^>]*>/gi,
    /<textarea\b[^>]*>/gi,
    
    // Data URLs and base64
    /data:text\/html/gi,
    /data:application\/javascript/gi,
  ];

  // SQL Injection Patterns
  static SQL_PATTERNS = [
    /(\b(ALTER|CREATE|DELETE|DROP|EXEC(UTE)?|INSERT( +INTO)?|MERGE|SELECT|UPDATE|UNION( +ALL)?)\b)/gi,
    /(;|\s|^)(-{2}|\/\*|\*\/)/gi,
    /\b(AND|OR|NOT)\s+\d+\s*=\s*\d+/gi,
    /\b(UNION|SELECT|INSERT|UPDATE|DELETE|CREATE|ALTER|DROP)\s/gi,
    /'(\s*(OR|AND)\s*)+'/gi,
  ];

  // Path Traversal Patterns
  static PATH_TRAVERSAL_PATTERNS = [
    /\.\.\//gi,
    /\.\.\\/gi,
    /%2e%2e%2f/gi,
    /%2e%2e%5c/gi,
    /\.\.%2f/gi,
    /\.\.%5c/gi,
  ];

  // Enhanced String Sanitization (extends existing ValidationService.sanitizeString)
  static sanitizeUserInput(input, options = {}) {
    const config = {
      maxLength: options.maxLength || 255,
      allowHTML: options.allowHTML || false,
      allowSpecialChars: options.allowSpecialChars !== false, // Default true
      stripScripts: options.stripScripts !== false, // Default true
      preventXSS: options.preventXSS !== false, // Default true
      preventSQLInjection: options.preventSQLInjection !== false, // Default true
      preventPathTraversal: options.preventPathTraversal !== false, // Default true
      trim: options.trim !== false, // Default true
      normalizeSpaces: options.normalizeSpaces !== false, // Default true
      ...options
    };

    try {
      if (!input || typeof input !== 'string') {
        return '';
      }

      let sanitized = input;

      // Basic trimming and normalization
      if (config.trim) {
        sanitized = sanitized.trim();
      }

      if (config.normalizeSpaces) {
        sanitized = sanitized.replace(/\s+/g, ' '); // Replace multiple spaces with single space
      }

      // Length limitation
      if (config.maxLength && sanitized.length > config.maxLength) {
        sanitized = sanitized.slice(0, config.maxLength);
      }

      // XSS Prevention
      if (config.preventXSS) {
        sanitized = this.removeXSSVectors(sanitized);
      }

      // SQL Injection Prevention
      if (config.preventSQLInjection) {
        sanitized = this.removeSQLInjectionVectors(sanitized);
      }

      // Path Traversal Prevention
      if (config.preventPathTraversal) {
        sanitized = this.removePathTraversalVectors(sanitized);
      }

      // HTML Handling
      if (!config.allowHTML) {
        sanitized = this.stripHTML(sanitized);
      } else if (config.stripScripts) {
        sanitized = this.stripDangerousHTML(sanitized);
      }

      // Special Character Handling
      if (!config.allowSpecialChars) {
        sanitized = this.removeSpecialCharacters(sanitized);
      }

      return sanitized;

    } catch (error) {
      ErrorTrackingService.captureError(error, {
        service: 'SanitizationService',
        method: 'sanitizeUserInput',
        inputLength: input?.length || 0,
        options: config,
        level: 'warning'
      });

      // Return safe fallback
      return input ? input.slice(0, 50).replace(/[<>]/g, '') : '';
    }
  }

  // Specialized sanitization methods for different input types
  static sanitizeEmail(email) {
    if (!email || typeof email !== 'string') return '';
    
    return this.sanitizeUserInput(email, {
      maxLength: 254, // RFC 5321 limit
      allowHTML: false,
      allowSpecialChars: true, // Emails need @ and . characters
      preventXSS: true,
      preventSQLInjection: true,
      preventPathTraversal: true
    }).toLowerCase();
  }

  static sanitizePhoneNumber(phone) {
    if (!phone || typeof phone !== 'string') return '';
    
    // Keep only digits, +, spaces, parentheses, and hyphens
    const cleaned = phone.replace(/[^\d\+\s\(\)\-]/g, '');
    
    return this.sanitizeUserInput(cleaned, {
      maxLength: 20,
      allowHTML: false,
      allowSpecialChars: true,
      preventXSS: true,
      preventSQLInjection: true,
      preventPathTraversal: true
    });
  }

  static sanitizeName(name) {
    if (!name || typeof name !== 'string') return '';
    
    return this.sanitizeUserInput(name, {
      maxLength: 100,
      allowHTML: false,
      allowSpecialChars: true, // Names can have apostrophes, hyphens, etc.
      preventXSS: true,
      preventSQLInjection: true,
      preventPathTraversal: true
    });
  }

  static sanitizeBusinessName(businessName) {
    if (!businessName || typeof businessName !== 'string') return '';
    
    return this.sanitizeUserInput(businessName, {
      maxLength: 100,
      allowHTML: false,
      allowSpecialChars: true, // Business names can have &, apostrophes, etc.
      preventXSS: true,
      preventSQLInjection: true,
      preventPathTraversal: true
    });
  }

  static sanitizeAddress(address) {
    if (!address || typeof address !== 'string') return '';
    
    return this.sanitizeUserInput(address, {
      maxLength: 200,
      allowHTML: false,
      allowSpecialChars: true, // Addresses need commas, periods, etc.
      preventXSS: true,
      preventSQLInjection: true,
      preventPathTraversal: true
    });
  }

  static sanitizeTextArea(text) {
    if (!text || typeof text !== 'string') return '';
    
    return this.sanitizeUserInput(text, {
      maxLength: 1000,
      allowHTML: false,
      allowSpecialChars: true,
      preventXSS: true,
      preventSQLInjection: true,
      preventPathTraversal: true,
      normalizeSpaces: false // Preserve formatting in text areas
    });
  }

  static sanitizeUrl(url) {
    if (!url || typeof url !== 'string') return '';
    
    // Only allow http, https, and mailto protocols
    const allowedProtocols = ['http:', 'https:', 'mailto:'];
    
    try {
      const urlObj = new URL(url);
      if (!allowedProtocols.includes(urlObj.protocol)) {
        return '';
      }
    } catch (error) {
      return ''; // Invalid URL
    }
    
    return this.sanitizeUserInput(url, {
      maxLength: 500,
      allowHTML: false,
      allowSpecialChars: true,
      preventXSS: true,
      preventSQLInjection: true,
      preventPathTraversal: false // URLs can have legitimate paths
    });
  }

  // Core sanitization helpers
  static removeXSSVectors(input) {
    let sanitized = input;
    
    this.XSS_PATTERNS.forEach(pattern => {
      sanitized = sanitized.replace(pattern, '');
    });
    
    // Remove dangerous attributes
    sanitized = sanitized.replace(/\s*on\w+\s*=\s*["'][^"']*["']/gi, '');
    
    // Remove javascript: and data: protocols
    sanitized = sanitized.replace(/(javascript|data|vbscript):/gi, '');
    
    return sanitized;
  }

  static removeSQLInjectionVectors(input) {
    let sanitized = input;
    
    this.SQL_PATTERNS.forEach(pattern => {
      sanitized = sanitized.replace(pattern, '');
    });
    
    // Remove common SQL injection characters
    sanitized = sanitized.replace(/[';]|--/g, '');
    
    return sanitized;
  }

  static removePathTraversalVectors(input) {
    let sanitized = input;
    
    this.PATH_TRAVERSAL_PATTERNS.forEach(pattern => {
      sanitized = sanitized.replace(pattern, '');
    });
    
    return sanitized;
  }

  static stripHTML(input) {
    return input.replace(/<[^>]*>/g, '');
  }

  static stripDangerousHTML(input) {
    // Remove dangerous tags but allow basic formatting
    const dangerousTags = [
      'script', 'iframe', 'object', 'embed', 'form', 'input', 
      'textarea', 'button', 'select', 'option', 'link', 'meta'
    ];
    
    let sanitized = input;
    dangerousTags.forEach(tag => {
      const pattern = new RegExp(`<${tag}\\b[^>]*>.*?<\\/${tag}>`, 'gi');
      sanitized = sanitized.replace(pattern, '');
      
      const selfClosingPattern = new RegExp(`<${tag}\\b[^>]*\\/?>`, 'gi');
      sanitized = sanitized.replace(selfClosingPattern, '');
    });
    
    return sanitized;
  }

  static removeSpecialCharacters(input) {
    // Keep only alphanumeric, spaces, and basic punctuation
    return input.replace(/[^\w\s\.\,\!\?\-\'\"]/g, '');
  }

  // Sanitization for form data objects
  static sanitizeFormData(formData, fieldConfig = {}) {
    const sanitized = {};
    
    Object.entries(formData).forEach(([key, value]) => {
      const config = fieldConfig[key] || {};
      
      if (typeof value === 'string') {
        sanitized[key] = this.sanitizeUserInput(value, config);
      } else if (Array.isArray(value)) {
        sanitized[key] = value.map(item => 
          typeof item === 'string' 
            ? this.sanitizeUserInput(item, config)
            : item
        );
      } else if (value && typeof value === 'object') {
        sanitized[key] = this.sanitizeFormData(value, config.nested || {});
      } else {
        sanitized[key] = value; // Numbers, booleans, null, etc.
      }
    });
    
    return sanitized;
  }

  // Enhanced validation that includes sanitization
  static validateAndSanitize(input, validationFn, sanitizationOptions = {}) {
    try {
      // First sanitize the input
      const sanitized = this.sanitizeUserInput(input, sanitizationOptions);
      
      // Then validate the sanitized input
      const isValid = validationFn(sanitized);
      
      return {
        isValid,
        sanitized,
        original: input,
        wasModified: sanitized !== input
      };
    } catch (error) {
      ErrorTrackingService.captureError(error, {
        service: 'SanitizationService',
        method: 'validateAndSanitize',
        level: 'warning'
      });
      
      return {
        isValid: false,
        sanitized: '',
        original: input,
        wasModified: true,
        error: error.message
      };
    }
  }

  // Batch sanitization for multiple inputs
  static sanitizeBatch(inputs, configs = {}) {
    const results = {};
    
    Object.entries(inputs).forEach(([key, value]) => {
      try {
        const config = configs[key] || {};
        results[key] = this.sanitizeUserInput(value, config);
      } catch (error) {
        ErrorTrackingService.captureError(error, {
          service: 'SanitizationService',
          method: 'sanitizeBatch',
          field: key,
          level: 'warning'
        });
        results[key] = ''; // Safe fallback
      }
    });
    
    return results;
  }

  // Security audit helpers
  static auditInput(input, context = '') {
    const findings = [];
    
    if (typeof input !== 'string') {
      return { safe: true, findings: [] };
    }
    
    // Check for potential XSS
    this.XSS_PATTERNS.forEach((pattern, index) => {
      if (pattern.test(input)) {
        findings.push({
          type: 'XSS',
          pattern: `XSS_PATTERN_${index}`,
          context,
          severity: 'high'
        });
      }
    });
    
    // Check for potential SQL injection
    this.SQL_PATTERNS.forEach((pattern, index) => {
      if (pattern.test(input)) {
        findings.push({
          type: 'SQL_INJECTION',
          pattern: `SQL_PATTERN_${index}`,
          context,
          severity: 'high'
        });
      }
    });
    
    // Check for path traversal
    this.PATH_TRAVERSAL_PATTERNS.forEach((pattern, index) => {
      if (pattern.test(input)) {
        findings.push({
          type: 'PATH_TRAVERSAL',
          pattern: `PATH_PATTERN_${index}`,
          context,
          severity: 'medium'
        });
      }
    });
    
    // Log security findings
    if (findings.length > 0) {
      ErrorTrackingService.addBreadcrumb(
        'Security audit finding',
        'security',
        'warning',
        { findings, context, inputLength: input.length }
      );
    }
    
    return {
      safe: findings.length === 0,
      findings,
      inputLength: input.length,
      context
    };
  }

  // Configuration presets for common use cases
  static getConfigPreset(type) {
    const presets = {
      'strict': {
        allowHTML: false,
        allowSpecialChars: false,
        maxLength: 100,
        preventXSS: true,
        preventSQLInjection: true,
        preventPathTraversal: true
      },
      'moderate': {
        allowHTML: false,
        allowSpecialChars: true,
        maxLength: 255,
        preventXSS: true,
        preventSQLInjection: true,
        preventPathTraversal: true
      },
      'permissive': {
        allowHTML: false,
        allowSpecialChars: true,
        maxLength: 1000,
        preventXSS: true,
        preventSQLInjection: true,
        preventPathTraversal: true,
        normalizeSpaces: false
      },
      'text_content': {
        allowHTML: false,
        allowSpecialChars: true,
        maxLength: 2000,
        preventXSS: true,
        preventSQLInjection: true,
        preventPathTraversal: true,
        normalizeSpaces: false
      }
    };
    
    return presets[type] || presets['moderate'];
  }

  // Health check method
  static performHealthCheck() {
    const testInputs = [
      '<script>alert("xss")</script>',
      "'; DROP TABLE users; --",
      '../../../etc/passwd',
      'javascript:alert("xss")',
      'normal text input'
    ];
    
    const results = testInputs.map(input => {
      const sanitized = this.sanitizeUserInput(input);
      const audit = this.auditInput(input, 'health_check');
      
      return {
        input,
        sanitized,
        safe: audit.safe,
        findings: audit.findings.length
      };
    });
    
    const allSafe = results.every(r => r.sanitized !== r.input || r.input === 'normal text input');
    
    return {
      healthy: allSafe,
      testResults: results,
      timestamp: new Date().toISOString()
    };
  }
}